'use client'
import Image from 'next/image'
import { useMemo } from 'react'
import { useLanguage } from '@/lib/context/LanguageContext'
import { brandLogos, type BrandLogo } from '@/lib/data/brandLogos'
import { assetUrl } from '@/lib/assetUrl'

// Curated brand list (slug = brand id in brandLogos.ts). Order here is
// the order they ride past on the strip.
//
// Two brands the user requested are not in the catalog and were
// dropped: 'snapp-pay' (Snapp Pay) and 'zar-makaron' (Zar Makaron).
// Drop new entries here once their logo assets are added.
const FEATURED_SLUGS = [
  'divar',
  'kaleh',
  'bank-saderat',
  'nobitex',
  'wallex',
  'iranserver',
  'blu-bank',
  'persian-gulf-petrochemicals',
  'silaneh-sabz',
  'mihan',
  'unilever',
  'cinnagen',
  'hamkadeh',
  'omran-kish',
  'zarrin-sanat',
  'tipax',
  'ministry-roads',
  'ministry-welfare',
] as const

const bySlug = new Map<string, BrandLogo>(brandLogos.map((b) => [b.slug, b]))
// Quietly drops any slug whose asset is missing — keeps the strip
// resilient if a brand mapping is renamed upstream.
const FEATURED: BrandLogo[] = FEATURED_SLUGS.map((s) => bySlug.get(s)).filter(
  (b): b is BrandLogo => b !== undefined,
)

// Edge fade so logos appear to enter from one side of the viewport
// and exit the other — no hard cut at the strip's edges.
const EDGE_MASK = 'linear-gradient(90deg, transparent 0%, black 7%, black 93%, transparent 100%)'

export default function BrandStrip() {
  const { lang } = useLanguage()

  // Double the list so the -50% translate keyframe wraps seamlessly
  // (the half-loop boundary lines up with the start of the second copy).
  const doubled = useMemo(() => [...FEATURED, ...FEATURED], [])

  return (
    <section
      aria-label={lang === 'fa' ? 'برندهای همکار' : 'Partner brands'}
      className="relative bg-white border-y border-ink/5 py-10 md:py-14 overflow-hidden"
    >
      <div
        // dir="ltr" pins flex flow regardless of page language — without
        // it, RTL flex parks the track off-screen and the strip stays
        // empty until the animation drags it back into view.
        dir="ltr"
        className="group overflow-hidden"
        style={{ maskImage: EDGE_MASK, WebkitMaskImage: EDGE_MASK }}
      >
        <div
          className="flex items-center marquee-track group-hover:[animation-play-state:paused]"
          style={{ width: 'max-content', animationDuration: '90s' }}
        >
          {doubled.map((brand, i) => (
            <div
              key={`${brand.slug}-${i}`}
              // pr (not gap on parent) so spacing rides with each item —
              // matches the -50% wrap math so the loop has no seam.
              className="flex-shrink-0 pr-12 sm:pr-16 md:pr-20 flex items-center justify-center"
            >
              <div className="relative h-8 sm:h-9 md:h-10 w-[120px] sm:w-[140px] md:w-[160px] opacity-55 grayscale transition-all duration-500 ease-out hover:opacity-100 hover:grayscale-0 hover:scale-110">
                <Image
                  src={assetUrl(brand.logo)}
                  alt={lang === 'fa' ? brand.fa : brand.en}
                  fill
                  sizes="160px"
                  className="object-contain"
                  unoptimized
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
